import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Divider,
  Container,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  Policy
} from '@mui/icons-material';
import ScrumAILogo from '../components/common/ScrumAILogo';

export const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--hp-bg)', color: 'var(--hp-text)', py: 4, px: { xs: 2, md: 4 } }}>
      
      {/* Top Bar */}
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid var(--hp-border)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{
                borderColor: 'var(--hp-border)',
                color: 'var(--hp-text)',
                bgcolor: 'var(--hp-surface-raised)',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '2px 2px 6px var(--hp-shadow-dark), -2px -2px 6px var(--hp-shadow-light)',
                '&:hover': { bgcolor: 'var(--hp-surface)' }
              }}
            >
              Back to Home
            </Button>
            <ScrumAILogo fontSize="1.2rem" iconSize={28} clickable onClick={() => navigate('/')} />
          </Box>

          <Button
            variant="contained"
            onClick={() => navigate('/workspace')}
            sx={{
              bgcolor: 'var(--hp-primary)',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(79, 107, 255, 0.3)',
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
            }}
          >
            Launch Workspace
          </Button>
        </Box>
      </Container>

      {/* Main Privacy Policy Document Container */}
      <Container maxWidth="md">
        <Box
          className="neumorphic-card"
          sx={{
            p: { xs: 3, md: 6 },
            bgcolor: 'var(--hp-surface-raised)',
            borderRadius: '24px',
            boxShadow: '10px 10px 30px var(--hp-shadow-dark), -10px -10px 30px var(--hp-shadow-light)',
            border: '1px solid var(--hp-border)'
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Policy sx={{ color: 'var(--hp-primary)', fontSize: 28 }} />
              <Chip label="Legal Documentation" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 700, fontSize: '0.75rem' }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '2.6rem' }, color: 'var(--hp-text)' }}>
              Privacy Policy
            </Typography>
            <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="var(--hp-text-secondary)">
                <strong>Effective Date:</strong> October 16, 2026
              </Typography>
              <Typography variant="body2" color="var(--hp-text-secondary)">
                <strong>Last Updated:</strong> October 16, 2026
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section 1 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              1. Introduction
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Welcome to ScrumAIApp ("we," "our," or "us"). ScrumAIApp is a project and meeting management application designed to help users organize their work and manage their schedules.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              This Privacy Policy explains how ScrumAIApp collects, uses, stores, and protects personal information when you access our website at <a href="https://scrumaiapp.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hp-primary)', fontWeight: 600 }}>https://scrumaiapp.com</a> and use our services.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              By using ScrumAIApp, you acknowledge the practices described in this Privacy Policy.
            </Typography>
          </Box>

          {/* Section 2 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              2. Information We Collect
            </Typography>
            
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 2, mb: 1, fontSize: '1.1rem', color: 'var(--hp-text)' }}>
              2.1 Account Information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1 }}>
              When you sign in using Google through Firebase Authentication, we may collect and process:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'var(--hp-text-secondary)', lineHeight: 1.7 }}>
              <li>Your name and email address.</li>
              <li>Your Google profile picture, if available.</li>
              <li>Your Firebase Authentication user ID.</li>
              <li>Account creation and authentication-related information.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              We use this information to create and manage your account, authenticate you, and provide access to ScrumAIApp.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, mt: 2.5, mb: 1, fontSize: '1.1rem', color: 'var(--hp-text)' }}>
              2.2 Google Calendar Information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1 }}>
              If you choose to connect Google Calendar, ScrumAIApp requests permission to access the calendar information necessary to provide the calendar features you select.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1 }}>
              Depending on the permissions you grant and the features you use, this information may include:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'var(--hp-text-secondary)', lineHeight: 1.7 }}>
              <li>Calendar IDs and calendar names.</li>
              <li>Your primary calendar designation.</li>
              <li>Your access permissions for individual calendars.</li>
              <li>Calendar event details, if you authorize and use features that require access to events.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              ScrumAIApp will request only the Google permissions necessary for the features you use. You can choose whether to connect Google Calendar, independently of signing in to ScrumAIApp.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, mt: 2.5, mb: 1, fontSize: '1.1rem', color: 'var(--hp-text)' }}>
              2.3 Information Stored in Firestore
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1 }}>
              ScrumAIApp uses Google Cloud Firestore to store application data, which may include:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'var(--hp-text-secondary)', lineHeight: 1.7 }}>
              <li>Your account profile and Firebase user ID.</li>
              <li>Account creation timestamps.</li>
              <li>Application preferences and settings.</li>
              <li>Other information required to provide features you choose to use.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              The information stored depends on the features you use and your interactions with the application.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, mt: 2.5, mb: 1, fontSize: '1.1rem', color: 'var(--hp-text)' }}>
              2.4 Technical Information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              We may process technical information needed to operate, maintain, secure, and troubleshoot ScrumAIApp, such as request logs, error information, and basic device or browser information where available.
            </Typography>
          </Box>

          {/* Section 3 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1 }}>
              We use collected information to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'var(--hp-text-secondary)', lineHeight: 1.7 }}>
              <li>Authenticate users and maintain their accounts.</li>
              <li>Display account information within ScrumAIApp.</li>
              <li>Retrieve and display Google Calendars that a user authorizes us to access.</li>
              <li>Provide scheduling and meeting management features that users choose to use.</li>
              <li>Maintain application functionality, reliability, and security.</li>
              <li>Diagnose technical issues and prevent unauthorized access.</li>
              <li>Comply with applicable legal obligations.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              We do not use Google user data for purposes unrelated to providing or improving the user-facing features of ScrumAIApp that you have authorized.
            </Typography>
          </Box>

          {/* Section 4 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              4. Google API Services and Calendar Data
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              ScrumAIApp uses Google API Services, including Google Calendar API, to provide calendar-related functionality when users grant permission.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Google Calendar data is accessed only after you authorize the requested permissions. ScrumAIApp uses this information to provide the calendar features made available in the application.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              ScrumAIApp does not sell Google user data or use it for advertising, including personalized advertising. We do not transfer Google user data to third parties except as necessary to provide or improve authorized features, comply with applicable law, or as otherwise permitted by Google's applicable policies and your consent.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Google user data is not used for generalized artificial intelligence or machine learning model training.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              We limit access to Google user data to the purposes disclosed in this policy and follow the Google API Services User Data Policy, including its Limited Use requirements.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              For information about Google's practices, please visit Google's Privacy Policy:{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hp-primary)', fontWeight: 600 }}>
                https://policies.google.com/privacy
              </a>
            </Typography>
          </Box>

          {/* Section 5 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              5. Data Storage and Security
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              ScrumAIApp uses Firebase Authentication, Cloud Firestore, and Google Cloud infrastructure to support its services.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              We take reasonable technical and organizational measures to protect personal information against unauthorized access, loss, misuse, alteration, or disclosure.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Access to stored information is restricted according to application requirements and applicable security controls. Information is transmitted over encrypted connections where supported.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              No method of electronic storage or transmission can be guaranteed to be completely secure.
            </Typography>
          </Box>

          {/* Section 6 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              6. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              We do not sell or rent personal information.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Information may be processed by service providers that help operate ScrumAIApp, including Google Firebase and Google Cloud services, solely as needed to provide, secure, and maintain the application.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              We may also disclose information when required by law, to protect the rights and security of users or the service, or as part of a business transfer, subject to appropriate safeguards and applicable legal requirements.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              We do not permit service providers to use Google user data for purposes prohibited by Google's applicable policies.
            </Typography>
          </Box>

          {/* Section 7 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              7. Google Calendar Permissions and Revocation
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Connecting Google Calendar is optional. You can decline the requested permission and continue using any ScrumAIApp features that do not require Calendar access.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              You can revoke ScrumAIApp's access to your Google Account through your Google Account's third-party connections settings:{' '}
              <a href="https://myaccount.google.com/connections" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hp-primary)', fontWeight: 600 }}>
                https://myaccount.google.com/connections
              </a>
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              After access is revoked, ScrumAIApp will no longer be able to retrieve Google Calendar information using the revoked authorization.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              Revoking permission does not necessarily delete information previously stored in ScrumAIApp. You may contact us to request deletion of associated data, subject to applicable legal requirements.
            </Typography>
          </Box>

          {/* Section 8 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              8. Data Retention and Deletion
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              We retain personal information only for as long as reasonably necessary to provide the service, maintain account functionality, meet legitimate operational requirements, and comply with applicable legal obligations.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              You may request deletion of your ScrumAIApp account and associated personal information by contacting us at the email address below.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              Where applicable, we will delete or de-identify personal information that is no longer required, subject to legal obligations and legitimate security requirements.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              If Google Calendar access tokens or other authorization credentials are stored by ScrumAIApp, they should be protected using appropriate security measures and deleted when they are no longer needed or when access is disconnected, subject to applicable requirements.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              Deleting your ScrumAIApp account does not delete events or calendars in your Google Account.
            </Typography>
          </Box>

          {/* Section 9 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              9. Your Privacy Rights
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1 }}>
              Depending on your location and applicable law, you may have the right to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'var(--hp-text-secondary)', lineHeight: 1.7 }}>
              <li>Request access to personal information associated with your account.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Request deletion of personal information.</li>
              <li>Withdraw previously granted Google Calendar access.</li>
              <li>Ask questions about how your information is processed.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              To exercise these rights, contact us using the details provided below.
            </Typography>
          </Box>

          {/* Section 10 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              10. Children's Privacy
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 1.5 }}>
              ScrumAIApp is not intended for children who are below the minimum age permitted to use the service under applicable law. We do not knowingly collect personal information from children in violation of applicable legal requirements.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              If you believe a child has provided personal information inappropriately, please contact us so that we can investigate and take appropriate action.
            </Typography>
          </Box>

          {/* Section 11 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              11. Third-Party Services
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              ScrumAIApp relies on third-party services, including Google, Firebase Authentication, Google Cloud, Firestore, and Google Calendar API. Your use of Google services is also subject to Google's applicable terms and privacy policies. ScrumAIApp is not responsible for the independent privacy practices of third-party services.
            </Typography>
          </Box>

          {/* Section 12 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              12. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)' }}>
              We may update this Privacy Policy when our services, data practices, or legal requirements change. We will publish the updated policy on this page and revise the "Last Updated" date. Where required, we will provide additional notice or obtain renewed consent before using personal information for a materially different purpose.
            </Typography>
          </Box>

          {/* Section 13 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--hp-text)' }}>
              13. Contact Us
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'var(--hp-text-secondary)', mb: 2 }}>
              For privacy questions, account deletion requests, or concerns about the handling of your personal information, contact:
            </Typography>

            <Box sx={{ p: 2.5, bgcolor: 'var(--hp-surface)', borderRadius: '14px', border: '1px solid var(--hp-border)' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--hp-text)', mb: 0.5 }}>
                Application: ScrumAIApp
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--hp-text-secondary)', mb: 0.5 }}>
                Website:{' '}
                <a href="https://scrumaiapp.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hp-primary)', fontWeight: 600 }}>
                  https://scrumaiapp.com
                </a>
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--hp-text-secondary)' }}>
                Privacy Contact Email:{' '}
                <a href="mailto:joinme@scrumaiapp.com" style={{ color: 'var(--hp-primary)', fontWeight: 600, textDecoration: 'none' }}>
                  joinme@scrumaiapp.com
                </a>
              </Typography>
            </Box>
          </Box>

        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 4, textAlign: 'center', mt: 4 }}>
        <Typography variant="caption" color="var(--hp-text-secondary)">
          ScrumAIApp • https://scrumaiapp.com • All Rights Reserved © {new Date().getFullYear()}
        </Typography>
      </Box>

    </Box>
  );
};

export default PrivacyPolicyPage;
